// Kalender-Utilities für die Termine-Seite (Refactoring 06/2026,
// extrahiert aus AppointmentsPage.js — Verhalten unverändert).
import React from 'react';
import { Bot, Globe, Smartphone } from 'lucide-react';

// Default Calendar constants (können über Einstellungen überschrieben werden)
export const DEFAULT_CALENDAR_START_HOUR = 6;  // 06:00
export const DEFAULT_CALENDAR_END_HOUR = 20;   // 20:00

// Iter 287 Fix: Termin-Anzeige immer in Schweizer Zeitzone, unabhängig
// davon ob der Browser auf UTC, MESZ oder anderem läuft. Sonst werden
// 09:00 (gespeichert als 09:00+02:00) in einem UTC-Browser als 07:00
// gerendert (falsche Position im Kalender).
// Edge-Case: Mindbody-Slots werden OHNE TZ-Suffix gespeichert (z.B.
// "2026-05-29T07:30:00") — diese Strings sind bereits in Schweizer
// Zeit. Wenn kein TZ-Indikator vorhanden, parsen wir die Stunde
// direkt aus dem String, statt `new Date(...)` (das die Zeit als
// Browser-Local interpretiert).
export const SWISS_TZ = 'Europe/Zurich';
const _TZ_REGEX = /(?:Z|[+-]\d{2}:?\d{2})$/;
export const getSwissHM = (isoString) => {
  if (!isoString) return { hour: 0, minute: 0 };
  try {
    // Naïve ISO (kein TZ-Suffix) → bereits Schweizer Zeit, direkt parsen
    if (typeof isoString === 'string' && !_TZ_REGEX.test(isoString)) {
      const m = isoString.match(/T(\d{2}):(\d{2})/);
      if (m) return { hour: parseInt(m[1], 10), minute: parseInt(m[2], 10) };
    }
    const d = new Date(isoString);
    const fmt = new Intl.DateTimeFormat('de-CH', {
      timeZone: SWISS_TZ, hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const parts = fmt.formatToParts(d);
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
    return { hour, minute };
  } catch {
    return { hour: 0, minute: 0 };
  }
};

// 08/2026 Bug-Fix: `currentDate.toISOString().split('T')[0]` liefert den
// UTC-Tag. Bei currentDate = lokal Mitternacht (z.B. nach Monats-Klick)
// ist das in CH-Sommerzeit der VORTAG → available-services/Blockade-Checks
// liefen gegen den falschen Tag. Immer diese Helper verwenden:
export const toLocalYMD = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Kalendertag eines ISO-Timestamps in Schweizer Zeit (YYYY-MM-DD)
export const getSwissYMD = (isoString) => {
  try {
    return new Date(isoString).toLocaleDateString('sv-SE', { timeZone: SWISS_TZ });
  } catch {
    return '';
  }
};

// 08/2026 Bug-Fix: Buchungen bauten die Startzeit bisher mit
// `new Date(day).setHours(h, m)` → das ist BROWSER-lokale Zeit. Auf einem
// Nicht-CH-Browser (z.B. UTC) wurde 06:30 als 06:30Z gespeichert und im
// Kalender (der immer Schweizer Zeit rendert) als 08:30 angezeigt (+2h).
// Dieser Helper wandelt eine Schweizer Wanduhr-Zeit (YMD + Stunde/Minute)
// verlässlich in einen UTC-ISO-String — unabhängig von der Browser-Zeitzone.
export const swissWallTimeToISO = (ymd, hour, minute) => {
  const [y, mo, d] = ymd.split('-').map(Number);
  // 1. Wanduhr-Zeit versuchsweise als UTC interpretieren
  const guessUTC = Date.UTC(y, mo - 1, d, hour, minute, 0, 0);
  // 2. Ermitteln, welche Uhrzeit Europe/Zurich zu diesem Instant anzeigt
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: SWISS_TZ, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = dtf.formatToParts(new Date(guessUTC)).reduce((a, x) => {
    a[x.type] = x.value; return a;
  }, {});
  let hh = parseInt(p.hour, 10); if (hh === 24) hh = 0;
  const zurichAsUTC = Date.UTC(+p.year, +p.month - 1, +p.day, hh, +p.minute, +p.second);
  // 3. Offset (Minuten, die Zürich vor UTC liegt) abziehen → echter Instant
  const offsetMin = (zurichAsUTC - guessUTC) / 60000;
  return new Date(guessUTC - offsetMin * 60000).toISOString();
};

export const getDateRangeForView = (date, view) => {
  const baseDate = new Date(date);
  let start = new Date(baseDate);
  let end = new Date(baseDate);

  if (view === 'week') {
    const dayIndex = (start.getDay() + 6) % 7; // Monday as start
    start.setDate(start.getDate() - dayIndex);
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 7);
    end.setMilliseconds(end.getMilliseconds() - 1);
  } else if (view === 'month') {
    start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
    end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 1);
    end.setHours(0, 0, 0, 0);
    end.setMilliseconds(end.getMilliseconds() - 1);
  } else {
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 1);
    end.setMilliseconds(end.getMilliseconds() - 1);
  }

  return { startDate: start.toISOString(), endDate: end.toISOString() };
};

// Helper function to render booking source icon
export const BookingSourceIcon = ({ source, size = "normal", bookedByName = "" }) => {
  const sizeClass = size === "large" ? "h-5 w-5 text-xs" : "h-4 w-4 text-[10px]";

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  switch (source) {
    case 'chatbot':
      return (
        <span className={`inline-flex items-center justify-center ${sizeClass} rounded-full bg-green-100`} title="Via WhatsApp Chatbot">
          <Bot className="h-3 w-3 text-green-600" strokeWidth={2.5} />
        </span>
      );
    case 'app':
      return (
        <span className={`inline-flex items-center justify-center ${sizeClass} rounded-full bg-blue-100`} title="Via Mobile App">
          <Smartphone className="h-3 w-3 text-blue-600" strokeWidth={2.5} />
        </span>
      );
    case 'web':
      // Web = Kunde hat selbst gebucht
      return (
        <span className={`inline-flex items-center justify-center ${sizeClass} rounded-full bg-teal-100`} title="Kunde hat online gebucht">
          <Globe className="h-3 w-3 text-teal-600" strokeWidth={2.5} />
        </span>
      );
    case undefined:
    case null:
    case '':
      // Kein Source = Admin/Trainer hat gebucht, zeige Initialen wenn verfügbar
      if (bookedByName) {
        return (
          <span className={`inline-flex items-center justify-center ${sizeClass} rounded-full bg-purple-100 font-semibold text-purple-700`} title={`Gebucht von ${bookedByName}`}>
            {getInitials(bookedByName)}
          </span>
        );
      }
      return null; // Kein Icon wenn unbekannt
    default:
      if (source.startsWith('trainer:')) {
        const trainerName = source.replace('trainer:', '');
        return (
          <span className={`inline-flex items-center justify-center ${sizeClass} rounded-full bg-purple-100 font-semibold text-purple-700`} title={`Gebucht von ${trainerName}`}>
            {getInitials(trainerName)}
          </span>
        );
      }
      if (source.startsWith('admin:')) {
        const adminName = source.replace('admin:', '');
        return (
          <span className={`inline-flex items-center justify-center ${sizeClass} rounded-full bg-red-100 font-semibold text-red-700`} title={`Gebucht von ${adminName}`}>
            {getInitials(adminName)}
          </span>
        );
      }
      return null;
  }
};
